// ─── Import / Export de recettes ──────────────────────────────────
// Deux formats :
//   • MoPCal     : JSON maison { app:'mopcal', type:'recettes', version, recettes:[…] }
//                  → fidélité parfaite (backup / transfert entre comptes/apps MoP).
//   • Schema.org : JSON-LD Recipe (https://schema.org/Recipe) → interop web
//                  (Google, Mealie, Paprika, sites de recettes…).
// L'import détecte automatiquement le format. Module PUR, porté à l'identique
// de MoPCal-v3 services/recettesIO.js (aucune dépendance Firestore/React).

// ── Helpers ─────────────────────────────────────────────────────────
// Durée ISO 8601 (« PT1H30M ») ⇆ minutes.
function minutesVersISO(min) {
  const m = Math.max(0, Math.round(Number(min) || 0));
  if (!m) return undefined;
  const h = Math.floor(m / 60), r = m % 60;
  return 'PT' + (h ? `${h}H` : '') + (r ? `${r}M` : (h ? '' : '0M'));
}
function isoVersMinutes(iso) {
  if (!iso || typeof iso !== 'string') return 0;
  const m = iso.match(/P(?:T)?(?:(\d+)H)?(?:(\d+)M)?/i);
  if (!m) return 0;
  return (parseInt(m[1] || 0, 10) * 60) + parseInt(m[2] || 0, 10);
}

// Un ingrédient MoPCal → chaîne lisible « 200 g farine ».
function ingredientVersTexte(i) {
  return [i.quantite, i.unite, i.nom].map(x => (x == null ? '' : String(x)).trim()).filter(Boolean).join(' ');
}

// Unités reconnues pour découper une chaîne « 200 g farine » → {quantite, unite, nom}.
const UNITES_CONNUES = ['kg', 'g', 'mg', 'l', 'cl', 'ml', 'dl', 'càs', 'càc', 'c.à.s', 'c.à.c', 'cs', 'cc',
  'pincée', 'pincées', 'sachet', 'sachets', 'gousse', 'gousses', 'tranche', 'tranches', 'bouquet',
  'boîte', 'boite', 'pot', 'pots', 'pcs', 'pc', 'u', 'unité', 'unités', 'verre', 'verres', 'cuillère', 'cuillères'];
export function parseIngredientTexte(txt) {
  const s = (txt || '').trim();
  if (!s) return { nom: '', quantite: '', unite: '', prix: null };
  const m = s.match(/^\s*([\d]+(?:[.,]\d+)?(?:\s*[-–]\s*\d+(?:[.,]\d+)?)?|½|¼|¾)\s*(.*)$/);
  if (!m) return { nom: s, quantite: '', unite: '', prix: null };
  const quantite = m[1].replace('½', '0.5').replace('¼', '0.25').replace('¾', '0.75').replace(',', '.');
  let reste = (m[2] || '').trim();
  let unite = '';
  const prem = reste.split(/\s+/)[0] || '';
  if (UNITES_CONNUES.includes(prem.toLowerCase())) { unite = prem; reste = reste.slice(prem.length).trim(); }
  reste = reste.replace(/^de\s+/i, '').replace(/^d'/i, '').trim();
  return { nom: reste || s, quantite, unite, prix: null };
}

// ── Normalisation d'une recette pour l'écriture (mêmes champs que services/recettes) ─
function normRecette(r) {
  return {
    nom:      (r.nom || '').trim(),
    portions: Math.max(1, Number(r.portions) || 1),
    tempsMin: Number(r.tempsMin) || 0,
    categorie: r.categorie || '',
    ingredients: (r.ingredients || [])
      .filter(i => (i.nom || '').trim())
      .map(i => ({ nom: (i.nom || '').trim(), quantite: (i.quantite != null ? String(i.quantite) : '').trim(), unite: (i.unite || '').trim(), prix: (i.prix != null && i.prix !== '') ? (Number(i.prix) || 0) : null })),
    etapes: (r.etapes || []).map(s => (s || '').trim()).filter(Boolean),
  };
}

// ── EXPORT ────────────────────────────────────────────────────────
export function recettesVersMoPCal(recettes) {
  return JSON.stringify({
    app: 'mopcal', type: 'recettes', version: 1,
    recettes: (recettes || []).map(normRecette),
  }, null, 2);
}

function recetteVersSchemaOrg(r) {
  const o = { '@type': 'Recipe', name: (r.nom || '').trim() };
  if (r.portions) o.recipeYield = String(r.portions);
  const iso = minutesVersISO(r.tempsMin);
  if (iso) o.totalTime = iso;
  o.recipeIngredient = (r.ingredients || []).filter(i => (i.nom || '').trim()).map(ingredientVersTexte);
  o.recipeInstructions = (r.etapes || []).filter(Boolean).map(text => ({ '@type': 'HowToStep', text }));
  return o;
}
export function recettesVersSchemaOrg(recettes) {
  const arr = (recettes || []).map(recetteVersSchemaOrg);
  const doc = arr.length === 1
    ? { '@context': 'https://schema.org', ...arr[0] }
    : { '@context': 'https://schema.org', '@graph': arr };
  return JSON.stringify(doc, null, 2);
}

// ── IMPORT (détection auto) ─────────────────────────────────────────
// Renvoie { recettes: [...normalisées], format: 'mopcal'|'schema'|null, erreur? }.
export function parseImportRecettes(texte) {
  let data;
  try { data = JSON.parse(texte); } catch { return { recettes: [], format: null, erreur: 'Fichier illisible (JSON invalide).' }; }

  if (data && (data.app === 'mopcal' || Array.isArray(data.recettes))) {
    return { recettes: (data.recettes || []).map(normRecette).filter(r => r.nom), format: 'mopcal' };
  }
  if (Array.isArray(data) && data.length && (data[0].etapes || data[0].ingredients) && !data[0]['@type']) {
    return { recettes: data.map(normRecette).filter(r => r.nom), format: 'mopcal' };
  }

  const candidats = [];
  const collect = (x) => {
    if (!x) return;
    if (Array.isArray(x)) { x.forEach(collect); return; }
    if (x['@type'] === 'Recipe' || (Array.isArray(x['@type']) && x['@type'].includes('Recipe'))) candidats.push(x);
    if (Array.isArray(x['@graph'])) x['@graph'].forEach(collect);
  };
  collect(data);
  if (candidats.length) {
    const recettes = candidats.map(rc => normRecette({
      nom: rc.name || '',
      portions: parseInt(String(rc.recipeYield || '').replace(/[^\d]/g, ''), 10) || 1,
      tempsMin: isoVersMinutes(rc.totalTime || rc.cookTime || rc.prepTime),
      ingredients: (Array.isArray(rc.recipeIngredient) ? rc.recipeIngredient : []).map(parseIngredientTexte),
      etapes: normEtapesSchema(rc.recipeInstructions),
    })).filter(r => r.nom);
    return { recettes, format: 'schema' };
  }
  return { recettes: [], format: null, erreur: 'Format non reconnu (ni MoPCal ni Schema.org/Recipe).' };
}

function normEtapesSchema(instr) {
  if (!instr) return [];
  if (typeof instr === 'string') return instr.split(/\n+/).map(s => s.trim()).filter(Boolean);
  const out = [];
  const push = (x) => {
    if (!x) return;
    if (typeof x === 'string') { out.push(x.trim()); return; }
    if (Array.isArray(x)) { x.forEach(push); return; }
    if (x['@type'] === 'HowToSection' && Array.isArray(x.itemListElement)) { x.itemListElement.forEach(push); return; }
    if (x.text) out.push(String(x.text).trim());
    else if (x.name) out.push(String(x.name).trim());
  };
  push(instr);
  return out.filter(Boolean);
}
